import React, { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import { getExtensions, enableExtension, disableExtension, getWorkspaceIdForApi } from './api';
import { useWorkspace } from './WorkspaceContext';
import { getToken } from './auth';

const ExtensionsContext = createContext({
  extensions: [],
  loading: true,
  loadError: false,
  isEnabled: () => false,
  enable: async () => {},
  disable: async () => {},
  reload: async () => {},
});

export const ExtensionsProvider = ({ children }) => {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const loadGenRef = useRef(0);
  const { currentWorkspace, loading: workspaceLoading } = useWorkspace();

  const load = useCallback(async () => {
    if (!getToken()) {
      setExtensions([]);
      setLoadError(false);
      setLoading(false);
      return;
    }

    const stableWorkspaceId = currentWorkspace?.id ?? getWorkspaceIdForApi();
    if (!stableWorkspaceId) {
      if (workspaceLoading) {
        setLoading(true);
        setLoadError(false);
        return;
      }
      setExtensions([]);
      setLoadError(false);
      setLoading(false);
      return;
    }

    const gen = ++loadGenRef.current;
    try {
      setLoading(true);
      setLoadError(false);
      const res = await getExtensions();
      if (gen !== loadGenRef.current) return;
      setExtensions(res.data?.extensions || []);
    } catch {
      if (gen !== loadGenRef.current) return;
      setExtensions([]);
      setLoadError(true);
    } finally {
      if (gen === loadGenRef.current) {
        setLoading(false);
      }
    }
  }, [currentWorkspace, workspaceLoading]);

  useLayoutEffect(() => {
    load();
  }, [load]);

  const isEnabled = useCallback(
    (id) => extensions.some((ext) => ext.id === id && ext.enabled),
    [extensions]
  );

  const enable = useCallback(
    async (id) => {
      await enableExtension(id);
      await load();
    },
    [load]
  );

  const disable = useCallback(
    async (id) => {
      await disableExtension(id);
      await load();
    },
    [load]
  );

  return (
    <ExtensionsContext.Provider
      value={{ extensions, loading, loadError, isEnabled, enable, disable, reload: load }}
    >
      {children}
    </ExtensionsContext.Provider>
  );
};

export const useExtensions = () => useContext(ExtensionsContext);
